import React from 'react';
import { Box, Button, CircularProgress, InputBase, Switch, Typography, type SwitchProps, type SxProps, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { NEW_LOOK_DARK, NEW_LOOK_LIGHT, type NewLookTokens } from 'src/theme/newLookTokens';

// =====================================================================================
// Building blocks of the Settings page (/settings): a left navigation column with search
// and grouped items, and sections made of rows (title + grey description on the left, the
// control on the right, a hairline between rows). Sizes and colours follow the reference
// design: 15px items and titles, 14px descriptions, 13px group labels, 16px headings.
// =====================================================================================

export const SETTINGS_FONT = {
  nav: 15,
  groupLabel: 13,
  heading: 16,
  title: 15,
  description: 14,
} as const;

/** The new-look colours — one source for the whole back office (src/theme/newLookTokens.ts). */
export type SettingsTokens = NewLookTokens;
const LIGHT: SettingsTokens = NEW_LOOK_LIGHT;
const DARK: SettingsTokens = NEW_LOOK_DARK;

export function useSettingsTokens(): SettingsTokens {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? DARK : LIGHT;
}

/** The toggle of the reference design: 40 × 22, blue when on, grey when off. */
export const SettingsToggle: React.FC<SwitchProps> = ({ sx, ...props }) => {
  const k = useSettingsTokens();
  return (
    <Switch
      disableRipple
      {...props}
      sx={[
        {
          width: 40,
          height: 22,
          p: 0,
          flexShrink: 0,
          overflow: 'visible',
          '& .MuiButtonBase-root, & .MuiSwitch-switchBase': {
            p: '3px',
            m: 0,
            color: '#fff',
            transitionDuration: '160ms',
            '&:hover': { bgcolor: 'transparent' },
          },
          '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)', color: '#fff' },
          // MUI's hidden input is 300% of the thumb (-22px…+44px) — with overflow visible it poked
          // past the 40px switch and made phone pages scroll sideways. Cover exactly the switch.
          '& .MuiSwitch-switchBase .MuiSwitch-input': { left: 0, width: 40 },
          '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-input': { left: -18 },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: k.accent, opacity: 1 },
          '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb, & .MuiSwitch-thumb': { bgcolor: '#fff' },
          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.45 },
          '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': { outline: `2px solid ${k.accent}`, outlineOffset: 2 },
          '& .MuiSwitch-thumb': { width: 16, height: 16, border: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.25)' },
          '& .MuiSwitch-track': { borderRadius: 11, bgcolor: k.toggleOff, opacity: 1, border: 0, boxShadow: 'none' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

/** A heading and its rows. */
export const SettingsSection: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Buttons shown on the right of the heading. */
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}> = ({ title, subtitle, actions, sx, children }) => {
  const k = useSettingsTokens();
  return (
    <Box component="section" sx={[{ mb: 5 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {(title || actions) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: subtitle ? 0.5 : '13px' }}>
          <Typography component="h2" sx={{ flex: 1, minWidth: 0, fontSize: SETTINGS_FONT.heading, fontWeight: 600, lineHeight: '24px', color: k.text }}>
            {title}
          </Typography>
          {actions && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{actions}</Box>}
        </Box>
      )}
      {subtitle && (
        <Typography sx={{ fontSize: SETTINGS_FONT.description, lineHeight: '20px', color: k.muted, mb: 1.25 }}>{subtitle}</Typography>
      )}
      <Box sx={{ '& > .settings-row:last-child': { borderBottom: 0 } }}>{children}</Box>
    </Box>
  );
};

/** One setting: title + description on the left, its control on the right. */
export const SettingsRow: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  control?: React.ReactNode;
  /** Shown under the title/description, full width (lists, chips, previews). */
  children?: React.ReactNode;
  /** Put the control under the text on narrow screens (wide inputs). */
  wideControl?: boolean;
  disabled?: boolean;
}> = ({ title, description, control, children, wideControl, disabled }) => {
  const k = useSettingsTokens();
  return (
    <Box className="settings-row" sx={{ py: '15px', borderBottom: `1px solid ${k.rowDivider}`, opacity: disabled ? 0.55 : 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexDirection: wideControl ? { xs: 'column', sm: 'row' } : 'row',
          ...(wideControl ? { alignItems: { xs: 'stretch', sm: 'center' } } : {}),
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: SETTINGS_FONT.title, lineHeight: '22px', color: k.text, fontWeight: 400 }}>
            {title}
          </Typography>
          {description && (
            <Typography component="div" sx={{ fontSize: SETTINGS_FONT.description, lineHeight: '20px', color: k.muted, mt: '3px' }}>
              {description}
            </Typography>
          )}
        </Box>
        {control && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>{control}</Box>}
      </Box>
      {children && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Box>
  );
};

/**
 * A usage meter row: title + grey note on the left, a thin bar in the middle, "31% used" on the
 * right. The bar turns amber from 90 % and red at the limit. `percent` null = no limit (no bar).
 */
export const UsageMeterRow: React.FC<{
  title: React.ReactNode;
  note?: React.ReactNode;
  percent: number | null;
  /** Right-hand text; defaults to "{percent}% used" (or "No limit"). */
  valueLabel?: React.ReactNode;
}> = ({ title, note, percent, valueLabel }) => {
  const k = useSettingsTokens();
  const pct = percent == null || Number.isNaN(percent) ? null : Math.max(0, Math.min(100, percent));
  const fill = pct == null ? k.accent : pct >= 100 ? '#DC2626' : pct >= 90 ? '#D97706' : k.accent;
  return (
    <Box
      className="settings-row"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr auto', sm: 'minmax(180px, 240px) 1fr 110px' },
        alignItems: 'center',
        columnGap: 3,
        rowGap: 1,
        py: '14px',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: SETTINGS_FONT.title, lineHeight: '22px', color: k.text }}>{title}</Typography>
        {note && <Typography sx={{ fontSize: SETTINGS_FONT.description, lineHeight: '20px', color: k.muted, mt: '6px' }}>{note}</Typography>}
      </Box>
      <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, gridRow: { xs: 2, sm: 'auto' } }}>
        {pct != null && (
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: k.accent === '#2A78D6' ? '#D6E4F7' : 'rgba(59,134,224,0.25)', overflow: 'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: fill, transition: 'width .3s' }} />
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: SETTINGS_FONT.description, color: k.muted, textAlign: 'right', whiteSpace: 'nowrap', gridRow: { xs: 1, sm: 'auto' }, gridColumn: { xs: 2, sm: 'auto' } }}>
        {valueLabel ?? (pct == null ? 'No limit' : `${Math.round(pct)}% used`)}
      </Typography>
    </Box>
  );
};

/** Compact outlined input style used for row controls. */
export function settingsFieldSx(k: SettingsTokens, width: number | string = 240): SxProps<Theme> {
  return {
    width: { xs: '100%', sm: width },
    '& .MuiOutlinedInput-root': {
      fontSize: SETTINGS_FONT.title,
      borderRadius: '8px',
      bgcolor: k.pageBg,
      '& fieldset': { borderColor: k.searchBorder },
      '&:hover fieldset': { borderColor: k.muted },
      '&.Mui-focused fieldset': { borderColor: k.accent, borderWidth: 1 },
    },
    '& .MuiInputBase-input': { py: '8px', fontSize: SETTINGS_FONT.title },
    '& .MuiFormHelperText-root': { mx: 0, fontSize: 12.5, color: k.muted, textTransform: 'none' },
  };
}

/** Dark primary button + quiet secondary button of the design. */
export function settingsButtonSx(k: SettingsTokens, variant: 'primary' | 'quiet' = 'primary'): SxProps<Theme> {
  return variant === 'primary'
    ? {
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 500,
        borderRadius: '8px',
        px: 1.75,
        py: '6px',
        boxShadow: 'none',
        bgcolor: k.buttonBg,
        color: k.buttonText,
        '&:hover': { bgcolor: k.buttonBg, opacity: 0.88, boxShadow: 'none' },
        '&.Mui-disabled': { bgcolor: k.buttonBg, color: k.buttonText, opacity: 0.4 },
      }
    : {
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 500,
        borderRadius: '8px',
        px: 1.5,
        py: '5px',
        color: k.text,
        border: `1px solid ${k.searchBorder}`,
        bgcolor: k.pageBg,
        '&:hover': { bgcolor: k.hover, borderColor: k.searchBorder },
      };
}

export interface SettingsNavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
}

/** Left column: search box, then grouped items ("Settings", "Customize"). */
export const SettingsNav: React.FC<{
  groups: Array<{ label: string; items: SettingsNavItem[] }>;
  selected: string;
  onSelect: (key: string) => void;
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  emptyText: string;
}> = ({ groups, selected, onSelect, search, onSearch, searchPlaceholder, emptyText }) => {
  const k = useSettingsTokens();
  const visible = groups.filter((g) => g.items.length > 0);
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          height: 36,
          px: '10px',
          borderRadius: '8px',
          border: `1px solid ${k.searchBorder}`,
          bgcolor: k.searchBg,
          '&:focus-within': { borderColor: k.accent },
        }}
      >
        <SearchOutlinedIcon sx={{ fontSize: 20, color: k.navText }} />
        <InputBase
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const first = visible[0]?.items[0];
              if (first) onSelect(first.key);
            } else if (e.key === 'Escape') {
              onSearch('');
            }
          }}
          placeholder={searchPlaceholder}
          inputProps={{ 'aria-label': searchPlaceholder }}
          sx={{ flex: 1, fontSize: SETTINGS_FONT.nav, color: k.text, '& input::placeholder': { color: k.muted, opacity: 1 } }}
        />
      </Box>

      {visible.length === 0 && <Typography sx={{ fontSize: SETTINGS_FONT.description, color: k.muted, px: 1, mt: 3 }}>{emptyText}</Typography>}

      {visible.map((group) => (
        <Box key={group.label} component="nav" aria-label={group.label} sx={{ mt: '28px' }}>
          <Typography sx={{ fontSize: SETTINGS_FONT.groupLabel, lineHeight: '18px', color: k.muted, px: 1, mb: '12px' }}>{group.label}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            {group.items.map((item) => {
              const active = item.key === selected;
              return (
                <Box
                  key={item.key}
                  component="button"
                  type="button"
                  onClick={() => onSelect(item.key)}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    all: 'unset',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    width: '100%',
                    height: 36,
                    pl: '10px',
                    pr: 1,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: SETTINGS_FONT.nav,
                    lineHeight: '20px',
                    color: active ? k.text : k.navText,
                    bgcolor: active ? k.selected : 'transparent',
                    '&:hover': { bgcolor: active ? k.selected : k.hover },
                    '&:focus-visible': { outline: `2px solid ${k.accent}`, outlineOffset: -2 },
                    '& .MuiSvgIcon-root': { fontSize: 20, color: active ? k.text : k.navText, flexShrink: 0 },
                  }}
                >
                  {item.icon}
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/** Floating bar shown while a section has unsaved changes. */
export const SettingsSaveBar: React.FC<{
  open: boolean;
  saving?: boolean;
  message: string;
  saveLabel: string;
  savingLabel: string;
  discardLabel: string;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ open, saving, message, saveLabel, savingLabel, discardLabel, onSave, onDiscard }) => {
  const k = useSettingsTokens();
  if (!open) return null;
  return (
    <Box sx={{ position: 'sticky', bottom: 16, zIndex: 6, display: 'flex', justifyContent: 'center', pointerEvents: 'none', mt: 2 }}>
      <Box
        role="status"
        sx={{
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 2,
          py: 1.25,
          borderRadius: '12px',
          border: `1px solid ${k.border}`,
          bgcolor: k.pageBg,
          boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
        }}
      >
        <Typography sx={{ fontSize: SETTINGS_FONT.description, color: k.text, mr: 1 }}>{message}</Typography>
        <Button onClick={onDiscard} disabled={saving} sx={settingsButtonSx(k, 'quiet')}>
          {discardLabel}
        </Button>
        <Button onClick={onSave} disabled={saving} sx={settingsButtonSx(k)} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {saving ? savingLabel : saveLabel}
        </Button>
      </Box>
    </Box>
  );
};
